import {
  Lead,
  ILeadAction,
  ILeadActivity,
  LeadDetails,
} from '../../api/models';
import {
  createAction,
  createActionWithPayload,
  IAction,
  IActionWithPayload,
} from '../utils';

const LEAD_CREATE_SUCCESS = 'LEAD/CREATE_SUCCESS';
const LEAD_CREATE_ERROR = 'LEAD/CREATE_ERROR';
const LEAD_CREATE_REQUEST = 'LEAD/CREATE_REQUEST';

const GET_PLANNED_ACTIVITY_SUCCESS = 'PLANNED_ACTIVITY/SUCCESS';
const GET_PLANNED_ACTIVITY_ERROR = 'PLANNED_AVCIVITY/ERROR';
const GET_PLANNED_ACTIVITY_REQUEST = 'PLANNED_ACTIVITY/REQUEST';

const GET_COMPLETED_ACTIVITY_SUCCESS = 'COMPLETED_ACTIVITY/SUCCESS';
const GET_COMPLETED_ACTIVITY_ERROR = 'COMPLETED_AVCIVITY/ERROR';
const GET_COMPLETED_ACTIVITY_REQUEST = 'COMPLETED_ACTIVITY/REQUEST';

const SET_FILTER_IN_COMPLETED_ACTIVITY = 'COMPLETED_ACTIVITY/SET_FILTER';

const LEAD_UPDATE_REQUEST = 'LEAD/UPDATE_REQUEST';
const LEAD_UPDATE_SUCCESS = 'LEAD/UPDATE_SUCCESS';
const LEAD_UPDATE_ERROR = 'LEAD/UPDATE_ERROR';

const LEAD_GET_REQUEST = 'LEAD/GET_REQUEST';
const LEAD_GET_SUCCESS = 'LEAD/GET_SUCCESS';
const LEAD_GET_ERROR = 'LEAD/GET_ERROR';

const LEAD_ACTION_GET_REQUEST = 'LEAD/ACTION_GET_REQUEST';
const LEAD_ACTION_GET_SUCCESS = 'LEAD/ACTION_GET_SUCCESS';
const LEAD_ACTION_GET_ERROR = 'LEAD/ACTION_GET_ERROR';

const LEAD_CLEAR_DATA = 'LEAD/LEAD_CLEAR_DATA';

export interface LeadState {
  leadDetails?: LeadDetails;
  plannedActivities?: IActivityListingDetails;
  doneActivities?: IActivityListingDetails;
  leadActions?: ILeadAction[];
  isDetailsLoading?: boolean;
}

export interface ILeadActivityDetails {
  plannedActivities?: ILeadActivity[];
  doneActivities?: ILeadActivity[];
}

export interface IActivityListingDetails {
  data?: ILeadActivity[];
  isLoading?: boolean;
  filter?: string;
  totalCount?: number;
  page?: number;
  ownerId?: string;
}

type GetPlannedActivityActionType = IActionWithPayload<
  typeof GET_PLANNED_ACTIVITY_REQUEST,
  { id: string }
>;

type GetPlannedActivityAllActionType = IActionWithPayload<
  typeof GET_PLANNED_ACTIVITY_REQUEST,
  { ids: string[] }
>;

type GetCompletedActivityActionType = IActionWithPayload<
  typeof GET_COMPLETED_ACTIVITY_REQUEST,
  { id: string; page?: number }
>;

type GetCompletedActivityAllActionType = IActionWithPayload<
  typeof GET_COMPLETED_ACTIVITY_REQUEST,
  { ids: string[]; page?: number }
>;
type SetFilterInDoneActivitiesActionType = IActionWithPayload<
  typeof SET_FILTER_IN_COMPLETED_ACTIVITY,
  { filter: string }
>;

type GetPlannedActivitySuccessActionType = IActionWithPayload<
  typeof GET_PLANNED_ACTIVITY_SUCCESS,
  { data: ILeadActivity[] }
>;

type GetCompletedActivitySuccessActionType = IActionWithPayload<
  typeof GET_COMPLETED_ACTIVITY_SUCCESS,
  { data: ILeadActivity[]; totalCount?: number; page?: number }
>;

type GetPlannedActivityErrorActionType = IAction<
  typeof GET_PLANNED_ACTIVITY_ERROR
>;

type GetCompletedActivityErrorActionType = IAction<
  typeof GET_COMPLETED_ACTIVITY_ERROR
>;

type CreateLeadActionType = IActionWithPayload<
  typeof LEAD_CREATE_SUCCESS,
  { leadDetails: Lead }
>;

type CreateLeadRequestActionType = IAction<typeof LEAD_CREATE_REQUEST>;

type CreateLeadErrorActionType = IAction<typeof LEAD_CREATE_ERROR>;

type UpdateLeadActionType = IActionWithPayload<
  typeof LEAD_UPDATE_SUCCESS,
  { leadDetails: Lead }
>;

type UpdateLeadRequestActionType = IAction<typeof LEAD_UPDATE_REQUEST>;

type UpdateLeadErrorActionType = IAction<typeof LEAD_UPDATE_ERROR>;

type GetLeadSuccessActionType = IActionWithPayload<
  typeof LEAD_GET_SUCCESS,
  { leadDetails: Lead }
>;

type GetLeadRequestActionType = IAction<typeof LEAD_GET_REQUEST>;

type GetLeadErrorActionType = IAction<typeof LEAD_GET_ERROR>;

type GetLeadActionSuccessType = IActionWithPayload<
  typeof LEAD_ACTION_GET_SUCCESS,
  { leadActions?: ILeadAction[] }
>;

type GetLeadActionRequestType = IAction<typeof LEAD_ACTION_GET_REQUEST>;

type GetLeadActionErrorType = IAction<typeof LEAD_ACTION_GET_ERROR>;

type ClearLeadActionType = IAction<typeof LEAD_CLEAR_DATA>;

type Actions =
  | CreateLeadActionType
  | CreateLeadRequestActionType
  | CreateLeadErrorActionType
  | UpdateLeadActionType
  | UpdateLeadRequestActionType
  | UpdateLeadErrorActionType
  | GetLeadSuccessActionType
  | GetLeadRequestActionType
  | GetLeadErrorActionType
  | GetLeadActionSuccessType
  | GetLeadActionRequestType
  | GetPlannedActivityActionType
  | GetPlannedActivitySuccessActionType
  | GetPlannedActivityErrorActionType
  | GetCompletedActivityActionType
  | GetCompletedActivitySuccessActionType
  | GetCompletedActivityErrorActionType
  | SetFilterInDoneActivitiesActionType
  | GetLeadActionErrorType
  | ClearLeadActionType;

// initial state
const initialState: LeadState = {};

export const createLeadAction = (data: {
  leadDetails: Lead;
}): CreateLeadActionType => {
  return createActionWithPayload(LEAD_CREATE_SUCCESS, data);
};

export const setFilterInDoneActivities = (data: {
  filter: string;
}): SetFilterInDoneActivitiesActionType => {
  return createActionWithPayload(SET_FILTER_IN_COMPLETED_ACTIVITY, data);
};

export const getPlannedActivitiesAction = (data: {
  id: string;
}): GetPlannedActivityActionType => {
  return createActionWithPayload(GET_PLANNED_ACTIVITY_REQUEST, data);
};

export const getPlannedActivitiesAllAction = (data: {
  ids: string[];
}): GetPlannedActivityAllActionType => {
  return createActionWithPayload(GET_PLANNED_ACTIVITY_REQUEST, data);
};

export const getPlannedActivitiesSuccessAction = (data: {
  data: ILeadActivity[];
}): GetPlannedActivitySuccessActionType => {
  return createActionWithPayload(GET_PLANNED_ACTIVITY_SUCCESS, data);
};

export const getPlannedActivitiesErrorAction = (): GetPlannedActivityErrorActionType => {
  return createAction(GET_PLANNED_ACTIVITY_ERROR);
};

export const getCompletedActivitiesAction = (data: {
  id: string;
  page?: number;
}): GetCompletedActivityActionType => {
  return createActionWithPayload(GET_COMPLETED_ACTIVITY_REQUEST, data);
};

export const getCompletedActivitiesAllAction = (data: {
  ids: string[];
  page?: number;
}): GetCompletedActivityAllActionType => {
  return createActionWithPayload(GET_COMPLETED_ACTIVITY_REQUEST, data);
};

export const getCompletedActivitiesSuccessAction = (data: {
  data: ILeadActivity[];
  page?: number;
  totalCount?: number;
}): GetCompletedActivitySuccessActionType => {
  return createActionWithPayload(GET_COMPLETED_ACTIVITY_SUCCESS, data);
};

export const getCompletedActivitiesErrorAction = (): GetCompletedActivityErrorActionType => {
  return createAction(GET_COMPLETED_ACTIVITY_ERROR);
};

export const createLeadRequestAction = (): CreateLeadRequestActionType => {
  return createAction(LEAD_CREATE_REQUEST);
};

export const createLeadErrorAction = (): CreateLeadErrorActionType => {
  return createAction(LEAD_CREATE_ERROR);
};

export const updateLeadAction = (data: {
  leadDetails: LeadDetails;
}): UpdateLeadActionType => {
  return createActionWithPayload(LEAD_UPDATE_SUCCESS, data);
};

export const updateLeadRequestAction = (): UpdateLeadRequestActionType => {
  return createAction(LEAD_UPDATE_REQUEST);
};

export const updateLeadErrorAction = (): UpdateLeadErrorActionType => {
  return createAction(LEAD_UPDATE_ERROR);
};

export const getLeadSuccessAction = (data: {
  leadDetails: Lead;
}): GetLeadSuccessActionType => {
  return createActionWithPayload(LEAD_GET_SUCCESS, data);
};

export const getLeadRequestAction = (): GetLeadRequestActionType => {
  return createAction(LEAD_GET_REQUEST);
};

export const getLeadErrorAction = (): GetLeadErrorActionType => {
  return createAction(LEAD_GET_ERROR);
};

export const getLeadActionSuccess = (data: {
  leadActions?: ILeadAction[];
}): GetLeadActionSuccessType => {
  return createActionWithPayload(LEAD_ACTION_GET_SUCCESS, data);
};

export const getLeadActionRequest = (): GetLeadActionRequestType => {
  return createAction(LEAD_ACTION_GET_REQUEST);
};

export const getLeadActionError = (): GetLeadActionErrorType => {
  return createAction(LEAD_ACTION_GET_ERROR);
};

export const clearLeadAction = (): ClearLeadActionType => {
  return createAction(LEAD_CLEAR_DATA);
};

const getActivityOwnerId = (payload: { id?: string; ids?: string[] }) =>
  payload.ids ? payload.ids.join(',') : payload.id;

// reducer
export const LeadReducer = (
  state: LeadState = initialState,
  action: Actions
): LeadState => {
  switch (action.type) {
    // create lead
    case LEAD_CREATE_SUCCESS:
      return {
        ...state,
        leadDetails: action.payload.leadDetails,
      };
    case GET_PLANNED_ACTIVITY_REQUEST:
      return {
        ...state,
        plannedActivities: {
          data: [],
          isLoading: true,
          ownerId: getActivityOwnerId(action.payload),
        },
      };
    case GET_COMPLETED_ACTIVITY_REQUEST:
      return {
        ...state,
        doneActivities: {
          ...state.doneActivities,
          data:
            action.payload.page && action.payload.page > 1
              ? state.doneActivities?.data
              : [],
          isLoading: true,
          ownerId: getActivityOwnerId(action.payload),
        },
      };
    case GET_PLANNED_ACTIVITY_ERROR:
      return {
        ...state,
        plannedActivities: {
          ...state.plannedActivities,
          data: [],
          isLoading: false,
        },
      };
    case GET_COMPLETED_ACTIVITY_ERROR:
      return {
        ...state,
        doneActivities: {
          ...state.doneActivities,
          data: [],
          isLoading: false,
        },
      };
    case GET_PLANNED_ACTIVITY_SUCCESS:
      return {
        ...state,
        plannedActivities: {
          ...state.plannedActivities,
          data: action.payload.data,
          isLoading: false,
        },
      };
    case GET_COMPLETED_ACTIVITY_SUCCESS:
      let doneActivityData = [...(state.doneActivities?.data ?? [])];
      if (!action.payload.page || action.payload.page === 1) {
        doneActivityData = [];
      }
      doneActivityData = doneActivityData.concat(action.payload.data);
      return {
        ...state,
        doneActivities: {
          ...state.doneActivities,
          data: doneActivityData,
          isLoading: false,
          totalCount: action.payload.totalCount,
          page: action.payload.page,
        },
      };
    case SET_FILTER_IN_COMPLETED_ACTIVITY:
      return {
        ...state,
        doneActivities: {
          ...state.doneActivities,
          filter: action.payload.filter,
        },
      };
    case LEAD_CREATE_REQUEST:
      return {
        ...state,
      };
    case LEAD_CREATE_ERROR:
      return {
        ...state,
      };
    // create customer
    case LEAD_UPDATE_SUCCESS:
      return {
        ...state,
        leadDetails: action.payload.leadDetails,
      };
    case LEAD_UPDATE_REQUEST:
      return {
        ...state,
      };
    case LEAD_UPDATE_ERROR:
      return {
        ...state,
      };

    case LEAD_GET_SUCCESS:
      return {
        ...state,
        leadDetails: action.payload.leadDetails,
        isDetailsLoading: false,
      };
    case LEAD_GET_REQUEST:
      return {
        ...state,
        leadDetails: undefined,
        isDetailsLoading: true,
      };
    case LEAD_GET_ERROR:
      return {
        ...state,
        isDetailsLoading: false,
      };

    case LEAD_ACTION_GET_SUCCESS:
      return {
        ...state,
        leadActions: action.payload.leadActions,
      };
    case LEAD_ACTION_GET_REQUEST:
      return {
        ...state,
        leadActions: undefined,
      };
    case LEAD_ACTION_GET_ERROR:
      return {
        ...state,
      };
    case LEAD_CLEAR_DATA:
      return {
        ...initialState,
        leadDetails: undefined,
        leadActions: undefined,
      };
    default:
      return state;
  }
};
